import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Key, Plus, Trash2, Copy, CheckCircle2, Shield, Clock, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const MODULE_LABELS = {
  core: 'Core Platform', accounting: 'Accounting & Finance', sales: 'Sales & POS',
  procurement: 'Procurement & Expenses', workforce: 'Workforce & HR',
  inventory: 'Inventory & Stock', analytics: 'Analytics & Reports', operations: 'Operations',
};

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ name: '', modules: [], expires_days: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    try { const res = await api.get('/v1/api-keys'); setKeys(res.data); }
    catch { toast.error('Failed to load API keys'); }
    finally { setLoading(false); }
  };

  const createKey = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setCreating(true);
    try {
      const res = await api.post('/v1/api-keys', {
        name: form.name,
        modules: form.modules,
        expires_days: form.expires_days ? parseInt(form.expires_days) : null,
      });
      setNewKey(res.data);
      setForm({ name: '', modules: [], expires_days: '' });
      fetchKeys();
    } catch { toast.error('Failed to create key'); }
    finally { setCreating(false); }
  };

  const revokeKey = async (id, name) => {
    if (!confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/v1/api-keys/${id}`); toast.success('Key revoked'); fetchKeys(); }
    catch { toast.error('Failed to revoke'); }
  };

  const toggleModule = (mod) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(mod)
        ? prev.modules.filter(m => m !== mod)
        : [...prev.modules, mod],
    }));
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  if (loading) return (
    <DashboardLayout><div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div></DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="api-keys-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-500 to-zinc-600 flex items-center justify-center shadow-lg shadow-stone-200/50">

                <Key size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="api-keys-title"></h1>

                <p className="text-sm text-muted-foreground mt-0.5">API key management</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Manage API keys for external system integrations (POS, HR, custom apps)
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="create-key-btn">
            <Plus size={14} className="mr-1" /> Create Key
          </Button>
        </div>

        {/* Info Banner */}
        <Card className="border-info/30 bg-info/10">
          <CardContent className="p-4 flex items-start gap-3">
            <Shield size={18} className="text-info mt-0.5 shrink-0" />
            <div className="text-sm text-info">
              <p className="font-medium">External apps use API keys instead of user login</p>
              <p className="text-info mt-1">
                Add <code className="bg-info/20 px-1 rounded">X-API-Key: your_key</code> header to API requests.
                Docs at <a href="/api/docs" target="_blank" className="underline font-medium">/api/docs</a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Newly Created Key (shown only once) */}
        {newKey && (
          <Card className="border-success/30 bg-success/10">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-success" />
                <span className="font-semibold text-success">API Key Created: {newKey.name}</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg p-3 border border-success/30">
                <code className="flex-1 text-sm font-mono break-all" data-testid="new-api-key">{newKey.api_key}</code>
                <Button variant="ghost" size="sm" onClick={() => copyKey(newKey.api_key)} data-testid="copy-key-btn">
                  <Copy size={14} />
                </Button>
              </div>
              <p className="text-xs text-success font-medium">
                Save this key now. It will not be shown again.
              </p>
              <Button variant="outline" size="sm" onClick={() => setNewKey(null)}>Dismiss</Button>
            </CardContent>
          </Card>
        )}

        {/* Keys List */}
        {keys.length === 0 && !newKey && (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center text-muted-foreground">
              <Key size={32} className="mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">No API keys yet</p>
              <p className="text-sm mt-1">Create a key to enable external app integrations</p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {keys.map(k => (
            <Card key={k.id} className={!k.is_active ? 'opacity-50' : ''}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                  <Key size={18} className="text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground" data-testid={`key-name-${k.id}`}>{k.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{k.key_prefix}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(k.modules || []).map(m => (
                      <Badge key={m} variant="outline" className="text-[10px]">{MODULE_LABELS[m] || m}</Badge>
                    ))}
                    {(!k.modules || k.modules.length === 0) && (
                      <Badge variant="outline" className="text-[10px] text-warning bg-warning/10">All modules</Badge>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground shrink-0 space-y-1">
                  {k.usage_count > 0 && <p>{k.usage_count} requests</p>}
                  {k.last_used_at && <p className="flex items-center gap-1"><Clock size={10} /> {new Date(k.last_used_at).toLocaleDateString()}</p>}
                </div>
                <Button variant="ghost" size="sm" className="text-error hover:text-error shrink-0" onClick={() => revokeKey(k.id, k.name)} data-testid={`revoke-key-${k.id}`}>
                  <Trash2 size={14} />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create Key Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Key Name</Label>
                <Input placeholder="e.g. POS Integration" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="key-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Expires In (days, optional)</Label>
                <Input type="number" placeholder="Leave empty for no expiry" value={form.expires_days} onChange={e => setForm(p => ({...p, expires_days: e.target.value}))} data-testid="key-expires-input" />
              </div>
              <div className="space-y-2">
                <Label>Module Access (empty = all modules)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(MODULE_LABELS).filter(([k]) => k !== 'core').map(([k, v]) => (
                    <label key={k} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${form.modules.includes(k) ? 'bg-orange-50 border-orange-300' : 'bg-white border-border hover:border-border'}`}>
                      <input type="checkbox" checked={form.modules.includes(k)} onChange={() => toggleModule(k)} className="accent-orange-500" />
                      <span className="text-sm">{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={createKey} disabled={creating} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-create-key">
                {creating ? 'Creating...' : 'Create Key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
